import { FamilyUnit, PossibleEmployerContributionDto } from '@zorro/clients';
import { isDefined } from '@zorro/common-utils';
import { formatCurrencyEnUsNoCents } from '@zorro/shared/formatters';
import { Group, Stack, Text } from '@zorro/web/design-system';
import { AllowanceInfoTooltip } from '@zorro/web/shared-ui';
import {
  calculateStipends,
  filterContributionsByFamilyUnit,
  hasStipendRange,
  isChildlessFamilyUnit,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { $enum } from 'ts-enum-util';

const allowanceInfoFamilyUnitConfig = (familyUnit: FamilyUnit) => {
  return $enum.mapValue(familyUnit).with({
    [FamilyUnit.EMPLOYEE_ONLY]: {
      label: 'Employee only',
    },
    [FamilyUnit.EMPLOYEE_SPOUSE]: {
      label: 'With spouse',
    },
    [FamilyUnit.EMPLOYEE_CHILD]: {
      label: 'With child(ren)',
      withTooltip: true,
    },
    [FamilyUnit.FAMILY]: {
      label: 'Entire family',
      withTooltip: true,
    },
  });
};

type AllowanceCardProps = {
  possibleEmployerContributions: PossibleEmployerContributionDto[];
  familyUnit: FamilyUnit;
};

const AllowanceCard = ({
  possibleEmployerContributions,
  familyUnit,
}: AllowanceCardProps) => {
  const { label, withTooltip } = allowanceInfoFamilyUnitConfig(familyUnit);
  const isChildless = isChildlessFamilyUnit(familyUnit);
  const allContributions = filterContributionsByFamilyUnit(
    possibleEmployerContributions,
    familyUnit
  );

  const rangeContributions = hasStipendRange(allContributions)
    ? allContributions
    : undefined;
  const { minimumStipend, maximumStipend } = calculateStipends(
    allContributions,
    isChildless
  );

  if (!isDefined(minimumStipend)) {
    return null;
  }

  return (
    <Stack
      bg="var(--color-background-general-note-primary)"
      miw="9rem"
      bdrs="md"
      gap={0}
      p="md"
    >
      <Text fw={600}>
        {rangeContributions && isDefined(maximumStipend)
          ? `${formatCurrencyEnUsNoCents(minimumStipend)}-${formatCurrencyEnUsNoCents(maximumStipend)}*`
          : formatCurrencyEnUsNoCents(minimumStipend)}
      </Text>
      <Group wrap="nowrap" gap="xs">
        <Text c="var(--color-text-default-secondary)" size="xs">
          {label}
        </Text>
        {withTooltip && rangeContributions && (
          <AllowanceInfoTooltip
            rangeContributions={rangeContributions}
            labelText={label}
            familyUnit={familyUnit}
          />
        )}
      </Group>
    </Stack>
  );
};

type Props = {
  onboardingPeriodId: string;
};

export const EmployeePagePlansAllowanceInfo = ({
  onboardingPeriodId,
}: Props) => {
  const { data: possibleEmployerContributions = [], isFetching } =
    useMonolithQuery({
      method:
        'contributionModelsControllerFindPossibleEmployerContributionsByAnyPeriod',
      params: [onboardingPeriodId],
    });

  if (isFetching) {
    return null;
  }

  return (
    <Group>
      {Object.values(FamilyUnit).map((familyUnit) => {
        return (
          <AllowanceCard
            key={familyUnit}
            familyUnit={familyUnit}
            possibleEmployerContributions={possibleEmployerContributions}
          />
        );
      })}
    </Group>
  );
};
